import React, { createContext, useEffect, useState } from 'react';
import { Cookies } from 'react-cookie';
import { CookieSetOptions } from 'universal-cookie';
import { Auth } from '@aws-amplify/auth';
import { amplifyAuthConfig, isValidConfig } from '../config/cognitoConfig';
import { AuthUtils } from '../utils/AuthUtils';
import jwtDecode from 'jwt-decode';

const COOKIE_TTL = 300000000;
const USER_AUTHORIZATION_TOKEN_KEY = 'userAuthorizationToken';
const USER_SESSION_STATE_KEY = 'userSessionState';
const LOGIN_VISIT_REDIRECT_PATH = 'loginVisitRedirectPath';
const USER_SESSION_REFRESHED = 'refreshed';
const USER_SESSION_CACHED = 'cached';

interface AppAuthenticationProps {
    children: React.ReactNode;
}

export interface UserData {
    alias: string;
    employeeId: string;
    firstName: string;
    lastName: string;
    email: string;
    departmentName: string;
    manager: string;
    country: string;
    isManager: boolean;
    businessUnit: string;
    jobCode: string;
    locationDescription: string;
    departmentNumber: string;
    posixGroups: string;
    description: string;
}

export interface UserContextProps {
    authenticatedUser: UserData;
    setAuthenticatedUser: React.Dispatch<React.SetStateAction<UserData>>;
    signOut: () => Promise<void>;
}

export const UserContext = createContext<UserContextProps>({} as UserContextProps);

/**
 * AppAuthentication Component
 * Handles the authentication flow
 * Automatically redirects to Midway if not authenticated
 */
export const AppAuthentication: React.FC<AppAuthenticationProps> = ({ children }) => {
    const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
    const [authenticatedUser, setAuthenticatedUser] = useState<UserData>({} as UserData);

    const cookies = new Cookies();
    const cookieStorageOptions: CookieSetOptions = {
        path: '/',
        secure: true,
        sameSite: 'strict',
        domain: window.location.hostname,
        expires: new Date(Date.now() + COOKIE_TTL),
    };

    const onAuthenticationSuccess = (authenticatedUser: any) => {
        const jwtToken = authenticatedUser.signInUserSession.idToken.jwtToken;
        // Remove old token before adding new one
        cookies.remove(USER_AUTHORIZATION_TOKEN_KEY);
        cookies.set(USER_AUTHORIZATION_TOKEN_KEY, jwtToken, cookieStorageOptions);
        
        const decodedIdToken: any = jwtDecode(jwtToken);
        const userDetails: UserData = {
            alias: decodedIdToken['custom:alias'] || decodedIdToken['custom:USER_NAME'] || decodedIdToken.preferred_username?.toLowerCase() || '',
            employeeId: decodedIdToken['custom:employeeId'] || decodedIdToken['custom:EMPLOYEE_ID'] || '',
            firstName: decodedIdToken.given_name || '',
            lastName: decodedIdToken.family_name || '',
            email: decodedIdToken.email || '',
            departmentName: decodedIdToken['custom:AMZN_DEPT_NAME'] || '',
            manager: decodedIdToken['custom:MANAGER'] || '',
            country: decodedIdToken['custom:CO'] || '',
            isManager: decodedIdToken['custom:isManager'] === 'true' || decodedIdToken['custom:AMZN_IS_MANAGER'] === 'true',
            businessUnit: decodedIdToken['custom:AMZN_BUSINESS_UNIT'] || '',
            jobCode: decodedIdToken['custom:AMZN_JOB_CODE'] || '',
            locationDescription: decodedIdToken['custom:AMZN_LOC_DESCR'] || '',
            departmentNumber: decodedIdToken['custom:DEPARTMENT_NUMBER'] || '',
            posixGroups: decodedIdToken['custom:posix_groups'] || '',
            description: decodedIdToken['custom:DESCRIPTION'] || ''
        };

        
        setAuthenticatedUser(userDetails);
        setIsUserAuthenticated(true);
    };

    const redirectForAuthentication = () => {
        cookies.set(USER_SESSION_STATE_KEY, USER_SESSION_REFRESHED, cookieStorageOptions);
        cookies.set(LOGIN_VISIT_REDIRECT_PATH, `${location.pathname}${location.search}`, cookieStorageOptions);
        
        if (amplifyAuthConfig && isValidConfig) {
            const cognitoUrl = `https://${amplifyAuthConfig.oauth.domain}/authorize?client_id=${amplifyAuthConfig.userPoolWebClientId}&response_type=${amplifyAuthConfig.oauth.responseType}&redirect_uri=${amplifyAuthConfig.oauth.redirectSignIn}`;
            
            window.location.replace(cognitoUrl);
        }
    };

    const signOut = async (): Promise<void> => {
        try {
            await Auth.signOut();
            cookies.remove(USER_AUTHORIZATION_TOKEN_KEY);
            cookies.remove(USER_SESSION_STATE_KEY);
            cookies.remove(LOGIN_VISIT_REDIRECT_PATH);
            setAuthenticatedUser({} as UserData);
            setIsUserAuthenticated(false);
            // Redirect to home page after sign out
            window.location.href = '/';
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    };

    useEffect(() => {
        console.log('AppAuthentication: Starting authentication check');
        
        AuthUtils.currentAuthenticatedUser()
            .then((authenticatedUser: any) => {
                const sessionState = cookies.get(USER_SESSION_STATE_KEY);
                
                if (sessionState === USER_SESSION_REFRESHED) {
                    cookies.set(USER_SESSION_STATE_KEY, USER_SESSION_CACHED, cookieStorageOptions);
                    const redirectPath = cookies.get(LOGIN_VISIT_REDIRECT_PATH);
                    if (redirectPath && redirectPath !== window.location.pathname + window.location.search) {
                        window.location.replace(redirectPath);
                        return;
                    }
                    onAuthenticationSuccess(authenticatedUser);
                } else if (sessionState === USER_SESSION_CACHED) {
                    console.log('AppAuthentication: Using cached session');
                    onAuthenticationSuccess(authenticatedUser);
                } else {
                    console.log('AppAuthentication: No valid session, redirecting to authentication');
                    redirectForAuthentication();
                }
            })
            .catch((error: any) => {
                console.error('AppAuthentication: User authentication failed:', error);
                
                
                redirectForAuthentication();
            });
    }, []);

    if (isUserAuthenticated) {
        return (
            <UserContext.Provider value={{ authenticatedUser, setAuthenticatedUser, signOut }}>
                <React.Fragment>{children}</React.Fragment>
            </UserContext.Provider>
        );
    } else {
        return (
            <div className="auth-loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                </div>
                <p>Authenticating...</p>
            </div>
        );
    }
};

export default AppAuthentication;
